import { AnimalWeightCategory, AnimalWeightCategoryInfo, WeightType, AnimalType } from '../models/animal';
import { TFunction } from 'i18next';

const AnimalWeightCategoryMapping: Record<AnimalWeightCategory, AnimalWeightCategoryInfo> = {
  [AnimalWeightCategory.Unknown]: { unit: WeightType.Kilograms, value: 0 },
  [AnimalWeightCategory.Light]: { unit: WeightType.Kilograms, value: 1.1 },
  [AnimalWeightCategory.Medium]: { unit: WeightType.Kilograms, value: 1.2 },
  [AnimalWeightCategory.Heavy]: { unit: WeightType.Kilograms, value: 1.3 }
};

export const AnimalWeightCategoryLabelKeys: Record<AnimalWeightCategory, string> = {
  [AnimalWeightCategory.Unknown]: 'f-weight-category-unknown',
  [AnimalWeightCategory.Light]: 'f-weight-category-light',
  [AnimalWeightCategory.Medium]: 'f-weight-category-medium',
  [AnimalWeightCategory.Heavy]: 'f-weight-category-heavy'
};

export const renderWeightCellDoubleWeightWithDate = (
  value: AnimalWeightCategoryInfo | null,
  displayUnit: WeightType,
  weightCategory?: AnimalWeightCategory
): string => {
  if (!value || !value.value || value.value <= 0) {
    if (!weightCategory) {
      return '-';
    }
    const mappedValue = AnimalWeightCategoryMapping[weightCategory];
    return renderWeightCellDoubleWeightWithDate(mappedValue, displayUnit, weightCategory);
  }

  if (displayUnit === WeightType.Grams) {
    // Is same unit, return as is without conversion and unit
    if (value.unit == displayUnit) {
      return `${value.value} g`;
    }

    // Convert from kg to g
    if (value.unit == WeightType.Kilograms && displayUnit == WeightType.Grams) {
      return `${value.value * 1000} g`;
    }
  }

  if (displayUnit === WeightType.Kilograms) {
    // Is same unit, return as is without conversion and unit
    if (value.unit == displayUnit) {
      return `${value.value.toFixed(1)} kg`;
    }

    // Convert from g to kg
    if (value.unit == WeightType.Grams && displayUnit == WeightType.Kilograms) {
      return `${(value.value / 1000).toFixed(1)} kg`;
    }
  }

  return '/';
};

/**
 * Gets the translated name for an animal type
 * @param animalType - The animal type number (using AnimalType enum values)
 * @param t - Translation function from useTranslation hook
 * @returns Translated animal type name
 */
export const getAnimalTypeName = (animalType: number, t: TFunction): string => {
  const typeMap: Record<AnimalType, string> = {
    [AnimalType.Unknown]: t('m-animal-type-unknown'),
    [AnimalType.Sow]: t('m-animal-type-sow'),
    [AnimalType.Gilt]: t('m-animal-type-gilt'),
    [AnimalType.Piglet]: t('m-animal-type-piglet'),
    [AnimalType.Fattening]: t('m-animal-type-fattening'),
    [AnimalType.Boar]: t('m-animal-type-boar'),
    [AnimalType.Cow]: t('m-animal-type-cow'),
    [AnimalType.Calf]: t('m-animal-type-calf'),
    [AnimalType.Goat]: t('m-animal-type-goat')
  };
  return typeMap[animalType as AnimalType] || t('f-animal-type-unknown');
};
